import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Container, CSSReset, Heading } from "@chakra-ui/react";
import { FormField } from "./FormField";
import { VendorSelectField } from "./VendorSelectField";
import { formContext, ModelFieldValues } from "../formContext";
import { omit, kebabCase } from "lodash";
import { Model, ModelInput } from "../queries";
import moment from "moment-timezone";

const formatLocalDateTime = (value?: string) => {
  if (!value) return;
  return moment(value).format("YYYY-MM-DD HH:mm:ss");
};

const getDefaultFormValues = (model?: Model) => {
  if (!model) {
    return;
  }

  const {
    displayName,
    reportId,
    workspaceId,
    launchDate,
    participantCount,
    slug,
    vendors,
    description,
    productId,
    dataset,
    embargoEndDate,
    roles,
  } = model;

  return {
    displayName,
    reportId,
    workspaceId,
    embargoEndDate: formatLocalDateTime(embargoEndDate),
    launchDate: formatLocalDateTime(launchDate),
    participantCount: (participantCount ?? 0)?.toString(),
    slug,
    vendors: vendors?.map((v) => omit(v, "__typename")),
    description,
    productId,
    dataset,
    drillDownsRole: roles.includes("DrillDowns"),
  };
};

interface ModelFormProps {
  model?: Model;
  onSubmit: (data: ModelInput) => void;
  isFormSubmitting: boolean;
  title: string;
  buttonText?: string;
  buttonLoadingText?: string;
}

export const ModelForm = (props: ModelFormProps): JSX.Element => {
  const {
    model,
    onSubmit,
    isFormSubmitting,
    title,
    buttonText,
    buttonLoadingText,
  } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    watch,
  } = useForm<ModelFieldValues>({
    defaultValues: getDefaultFormValues(model),
  });

  const didMount = React.useRef(false);

  const displayName = watch("displayName");
  useEffect(() => {
    if (didMount.current) {
      setValue("slug", kebabCase(getValues("displayName")));
    }
    didMount.current = true;
  }, [displayName]);

  const handleFormSubmit = (data: ModelFieldValues) => {
    const roles = [];

    if (data.drillDownsRole) {
      roles.push("DrillDowns");
    }

    const variables = {
      roles,
      description: data.description,
      displayName: data.displayName,
      launchDate: new Date(data.launchDate).toISOString(),
      embargoEndDate: data.embargoEndDate
        ? new Date(data.embargoEndDate).toISOString()
        : null,
      participantCount: parseInt(data.participantCount),
      reportId: data.reportId,
      productId: data.productId,
      slug: data.slug,
      vendors: data.vendors?.map((v) => v.name) || [],
      workspaceId: data.workspaceId,
      dataset: "DRILLDOWN",
    };

    onSubmit(variables);
  };

  const currentTimeZone = moment().tz(moment.tz.guess()).format("z");

  return (
    <Container maxW={1000}>
      <formContext.Provider value={{ register, errors, control }}>
        <CSSReset />
        <Box
          borderWidth={{ lg: 1 }}
          p={{ lg: 5 }}
          borderRadius={{ lg: 5 }}
          shadow={{ lg: "lg" }}
        >
          <Heading>{title}</Heading>
          <form
            style={{ marginTop: 15 }}
            onSubmit={handleSubmit(handleFormSubmit)}
            noValidate
          >
            <FormField name="Display Name" />
            <FormField
              name="Slug"
              onBlur={() => {
                setValue("slug", kebabCase(getValues("slug")));
              }}
            />
            <FormField name="Workspace ID" />
            <FormField name="Report ID" />
            <FormField name="Product ID" required={false} />
            <FormField name="Participant Count" type="number" />
            <FormField
              name="Description"
              label="Description (200 character max)"
              type="textarea"
              maxLength={200}
            />
            <VendorSelectField />
            <FormField
              name="Launch Date"
              label={`Launch Date (${currentTimeZone})`}
              type="datetime-local"
            />
            <FormField
              name="Embargo End Date"
              label={`Embargo End Date (${currentTimeZone})`}
              type="datetime-local"
              required={false}
            />
            <FormField
              required={false}
              label="Allow users with Drill Downs access to view?"
              name="Drill Downs Role"
              type="checkbox"
            />
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isFormSubmitting}
              loadingText={buttonLoadingText || "Submitting"}
              data-cy="submit-button"
            >
              {buttonText || "Submit"}
            </Button>
          </form>
        </Box>
      </formContext.Provider>
    </Container>
  );
};
